import { Button, Divider, InputLabel, TextField, IconButton } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ApiRequest } from "GlobalFunctions";
import { useState, useEffect } from "react";
import { useSnackbar } from 'notistack';
import { useLocation } from "react-router-dom";
import { PiEyeSlashLight, PiEyeLight } from "react-icons/pi";
import { PiCheckLight } from "react-icons/pi";
import { PiXLight } from "react-icons/pi";

export default function ResetPassword(props: any) {
    const { setLoading } = props;
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loadingButton, setLoadingButton] = useState(false);

    // Get reset ID from URL parameters
    const getResetId = () => {
        const searchParams = new URLSearchParams(location.search);
        return searchParams.get('rid');
    };

    // Get email from URL parameters
    const getEmail = () => {
        const searchParams = new URLSearchParams(location.search);
        const email = searchParams.get('email');
        return email ? decodeURIComponent(email) : '';
    };

    const [passwordRequirements, setPasswordRequirements] = useState([
        { text: 'A minimum of 10 characters', valid: false },
        { text: 'at least one number', valid: false },
        { text: 'at least one lowercase letter', valid: false },
        { text: 'at least one uppercase letter', valid: false }
    ]);

    useEffect(() => {
        setPasswordRequirements([
            { text: 'A minimum of 10 characters', valid: newPassword.length >= 10 },
            { text: 'at least one number', valid: /\d/.test(newPassword) },
            { text: 'at least one lowercase letter', valid: /[a-z]/.test(newPassword) },
            { text: 'at least one uppercase letter', valid: /[A-Z]/.test(newPassword) }
        ]);
    }, [newPassword]);

    const validatePassword = () => {
        let valid = true;
        passwordRequirements.forEach(requirement => {
            if(!requirement.valid) valid = false;
        });

        if(!valid){
            enqueueSnackbar('Password Is Invalid', { variant: 'error' });
            return false;
        }

        if(newPassword !== confirmPassword) {
            enqueueSnackbar('Passwords do not match', { variant: 'error' });
            return false;
        }

        return true;
    }

    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleSubmit = async () => {
        setLoadingButton(true);
        
        // Validate required fields
        if (!newPassword || !confirmPassword) {
            enqueueSnackbar('All fields are required', { variant: 'error' });
            setLoadingButton(false);
            return;
        }

        const passwordValid = validatePassword();

        if(passwordValid){
            const resetId = getResetId();
            const email = getEmail();
            
            // Prepare the request body
            const requestBody = {
                email: email,
                password: newPassword,
                reset_code: resetId
            };
            
            await ApiRequest({
                url: '/users/change_password_auth',
                method: 'POST',
                body: requestBody,
                setResponse: (response: any) => {
                    if(response.status === "success"){
                        enqueueSnackbar('Password reset successfully', { variant: 'success' });
                        // Redirect to login after successful reset
                        window.location.href = '/login';
                    } else {
                        enqueueSnackbar(response.message, { variant: 'error' });
                    }
                    setLoadingButton(false);
                }
            });
        } else {
            setLoadingButton(false);
        }
    };

    return (
        <div className="forgot_password_container">

            <div>
                <h2>Reset Password</h2>
                <p>Choose a new password that hasn’t been used with this account before.</p>
                <Divider style={{ margin: '20px 0', borderColor: '#ECEBFF' }} />


                <form 
                className="forgot_password_form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                }}>
                    <div className="textfield_wrapper">
                        <InputLabel className="login_label">New Password</InputLabel>
                        <TextField
                            variant="outlined"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            type={showPassword ? "text" : "password"}
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <IconButton 
                                        onClick={handleTogglePasswordVisibility}
                                        edge="end"
                                        className="show_password_button"
                                    >
                                        {showPassword ? 
                                            <PiEyeLight size={24} color="#B9B9B9" /> : 
                                            <PiEyeSlashLight size={24} color="#B9B9B9"/>
                                        }
                                    </IconButton>
                                )
                            }}
                        />
                    </div>

                    <div className="textfield_wrapper">
                        <InputLabel className="login_label">Confirm Password</InputLabel>
                        <TextField
                            variant="outlined"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            type={showPassword ? "text" : "password"}
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <IconButton 
                                        onClick={handleTogglePasswordVisibility}
                                        edge="end"
                                        className="show_password_button"
                                    >
                                        {showPassword ? 
                                            <PiEyeLight size={24} color="#B9B9B9" /> : 
                                            <PiEyeSlashLight size={24} color="#B9B9B9"/>
                                        }
                                    </IconButton>
                                )
                            }}
                        />
                    </div>

                    <ul className="password_requirements" style={{ listStyle: 'none', padding: 0 }}>
                        {passwordRequirements.map((requirement, index) => (
                            <li 
                                key={index} 
                                className="password_requirement" 
                                style={{ 
                                    color: requirement.valid ? '#24BD55' : '#CCCCCC',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                {requirement.valid ? 
                                    <PiCheckLight size={16} color="#24BD55" style={{marginRight: 8}} /> : 
                                    <PiXLight size={16} color="#CCCCCC" style={{marginRight: 8}} />
                                }
                                {requirement.text}
                            </li>
                        ))}
                    </ul>

                    <LoadingButton
                        loading={loadingButton}
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        className="forgot_password_button"
                    >
                        Reset Password
                    </LoadingButton>
                </form>
            </div>
        </div>
    )
}